/**
 * CakeSegment — Customer-facing cake browser on Instant Pricing (birthday only).
 *
 * UX: ONE big swipeable cake card at a time. Customer's egg/weight/flavour
 * selection (always visible below the image) drives which cakes appear in
 * the swipe sequence and in what order.
 *
 * Ordering: cakes from vendors whose `city` matches the customer's
 * `eventCity` come first; everything else after.
 *
 * Hybrid filter (option c): strict match → relax flavour → relax weight →
 * relax egg type → show everything. A "Showing closest match" banner
 * indicates whenever the strict filter fell through.
 *
 * Tap image → full-screen lightbox with the same swipe + selection chips.
 */
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Cake, CaretDown, CaretUp, Plus, Check, CaretLeft, CaretRight, X as XIcon,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  return `${BACKEND_URL}${u}`;
};

const fmtWeight = (w) => String(Number(w));

// Reusable chip row — defined at module scope so React doesn't remount it
// every render (which would lose focus, scroll position, etc.).
const SelectionChips = ({
  compact = false,
  hasEgg, hasEggless,
  eggMode, setEggMode,
  allWeights, weight, setWeight,
  allFlavours, flavour, setFlavour,
}) => (
  <div className={compact ? 'space-y-2' : 'space-y-3'}>
    {(hasEgg || hasEggless) && (
      <div className="flex gap-2">
        {hasEgg && (
          <button onClick={() => setEggMode('egg')}
            className={`text-xs px-3 py-1.5 border rounded-none ${eggMode === 'egg' ? 'bg-amber-500 text-white border-amber-500' : 'border-[#E8E1D5] bg-white hover:bg-amber-50'}`}
            data-testid="cake-eggmode-egg">With Egg</button>
        )}
        {hasEggless && (
          <button onClick={() => setEggMode('eggless')}
            className={`text-xs px-3 py-1.5 border rounded-none ${eggMode === 'eggless' ? 'bg-green-600 text-white border-green-600' : 'border-[#E8E1D5] bg-white hover:bg-green-50'}`}
            data-testid="cake-eggmode-eggless">Eggless</button>
        )}
      </div>
    )}
    {allWeights.length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        <span className="text-[10px] uppercase tracking-wider font-bold text-text-secondary mr-1 self-center">Weight</span>
        {allWeights.map((w) => (
          <button key={w} onClick={() => setWeight(w)}
            className={`text-xs px-2.5 py-1 border rounded-none ${weight === w ? 'bg-brand-primary text-sand-50 border-brand-primary' : 'border-[#E8E1D5] bg-white hover:bg-sand-100'}`}
            data-testid={`cake-weight-${w}`}>{w} kg</button>
        ))}
      </div>
    )}
    {allFlavours.length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        <span className="text-[10px] uppercase tracking-wider font-bold text-text-secondary mr-1 self-center">Flavour</span>
        {allFlavours.map((fl) => (
          <button key={fl} onClick={() => setFlavour(fl)}
            className={`text-xs px-2.5 py-1 border rounded-none ${flavour === fl ? 'bg-purple-600 text-white border-purple-600' : 'border-[#E8E1D5] bg-white hover:bg-purple-50'}`}
            data-testid={`cake-flavour-${fl.toLowerCase().replace(/\s+/g, '_')}`}>{fl}</button>
        ))}
      </div>
    )}
  </div>
);

// Does `pkg` actually price this combo? When `weight` or `flavour` is null,
// "offers" means at least one row in the matrix would match.
const cakeOffers = (pkg, eggMode, weight, flavour) => {
  const matrix = eggMode === 'egg' ? pkg.prices_egg : pkg.prices_eggless;
  if (!matrix || Object.keys(matrix).length === 0) return false;
  if (flavour && !matrix[flavour]) return false;
  if (flavour && weight) return matrix[flavour][weight] !== undefined;
  if (!flavour && weight) {
    return Object.values(matrix).some((row) => row && row[weight] !== undefined);
  }
  return true;
};

const CakeSegment = ({ eventCity, expanded, onToggle, cart, setCart }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  // Global selection — drives the carousel sequence.
  const [eggMode, setEggMode] = useState('egg');
  const [weight, setWeight] = useState(null);
  const [flavour, setFlavour] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartX = useRef(null);

  // ----- Fetch -------------------------------------------------------------
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = eventCity ? { city: eventCity } : {};
    axios.get(`${BACKEND_URL}/api/cake-packages`, { params })
      .then(({ data }) => { if (alive) setVendors(data.vendors || []); })
      .catch(() => { if (alive) setVendors([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [eventCity]);

  // ----- Flatten & proximity-sort once -------------------------------------
  const allCakes = useMemo(() => {
    const list = [];
    vendors.forEach((v) => (v.packages || []).forEach((p) => list.push({ ...p, vendor: v })));
    const evCity = (eventCity || '').toLowerCase().trim();
    list.sort((a, b) => {
      const aClose = (a.vendor.city || '').toLowerCase().trim() === evCity ? 0 : 1;
      const bClose = (b.vendor.city || '').toLowerCase().trim() === evCity ? 0 : 1;
      if (aClose !== bClose) return aClose - bClose;
      return (a.vendor.business_name || '').localeCompare(b.vendor.business_name || '');
    });
    return list;
  }, [vendors, eventCity]);

  // ----- Aggregate options for the chip rows -------------------------------
  // Union of weights/flavours across all matching cakes (for the chosen egg mode).
  const { allWeights, allFlavours, hasEgg, hasEggless } = useMemo(() => {
    const wSet = new Set();
    const fSet = new Set();
    let he = false, hel = false;
    allCakes.forEach((p) => {
      if (Object.keys(p.prices_egg || {}).length) he = true;
      if (Object.keys(p.prices_eggless || {}).length) hel = true;
      const matrix = eggMode === 'egg' ? p.prices_egg : p.prices_eggless;
      Object.entries(matrix || {}).forEach(([fl, row]) => {
        if (row && Object.values(row).some((v) => v != null)) fSet.add(fl);
        Object.keys(row || {}).forEach((wk) => {
          if (row[wk] != null) wSet.add(String(Number(wk)));
        });
      });
    });
    const ws = [...wSet].sort((a, b) => Number(a) - Number(b));
    const fs = [...fSet].sort();
    return { allWeights: ws, allFlavours: fs, hasEgg: he, hasEggless: hel };
  }, [allCakes, eggMode]);

  // ----- Set defaults once data + options are known ------------------------
  useEffect(() => {
    if (!weight && allWeights.length) {
      // Prefer 1 kg, else first available.
      setWeight(allWeights.includes('1') ? '1' : allWeights[0]);
    }
    if (!flavour && allFlavours.length) {
      // Pick the flavour with the broadest coverage across cakes.
      const counts = {};
      allCakes.forEach((p) => {
        const matrix = eggMode === 'egg' ? p.prices_egg : p.prices_eggless;
        Object.keys(matrix || {}).forEach((fl) => { counts[fl] = (counts[fl] || 0) + 1; });
      });
      const top = allFlavours.slice().sort((a, b) => (counts[b] || 0) - (counts[a] || 0))[0];
      setFlavour(top || allFlavours[0]);
    }
    // Initial egg mode: pick the one that actually has any cakes.
    if (!hasEgg && hasEggless && eggMode === 'egg') setEggMode('eggless');
  }, [allWeights, allFlavours, allCakes, hasEgg, hasEggless, eggMode, weight, flavour]);

  // ----- Hybrid filter -----------------------------------------------------
  const { items: carouselItems, mode: filterMode } = useMemo(() => {
    if (allCakes.length === 0) return { items: [], mode: 'empty' };
    const tryFilter = (egg, w, fl) => allCakes.filter((p) => cakeOffers(p, egg, w, fl));
    const strict = tryFilter(eggMode, weight, flavour);
    if (strict.length) return { items: strict, mode: 'strict' };
    const relaxFlavour = tryFilter(eggMode, weight, null);
    if (relaxFlavour.length) return { items: relaxFlavour, mode: 'relaxFlavour' };
    const relaxWeight = tryFilter(eggMode, null, flavour);
    if (relaxWeight.length) return { items: relaxWeight, mode: 'relaxWeight' };
    const relaxEgg = tryFilter(eggMode === 'egg' ? 'eggless' : 'egg', null, null);
    if (relaxEgg.length) return { items: relaxEgg, mode: 'relaxEgg' };
    return { items: allCakes, mode: 'relaxAll' };
  }, [allCakes, eggMode, weight, flavour]);

  // Clamp activeIdx whenever the carousel changes.
  useEffect(() => {
    if (activeIdx >= carouselItems.length) setActiveIdx(0);
  }, [carouselItems.length, activeIdx]);

  const active = carouselItems[activeIdx];

  // Compute price for current selection (with fallbacks when filter relaxed).
  const computePrice = useCallback((pkg) => {
    if (!pkg) return null;
    const matrix = eggMode === 'egg' ? pkg.prices_egg : pkg.prices_eggless;
    if (!matrix) return null;
    if (flavour && weight && matrix[flavour]?.[weight] != null) {
      return matrix[flavour][weight];
    }
    // Best-effort fallback: same flavour, any weight closest to picked
    if (flavour && matrix[flavour]) {
      const ws = Object.keys(matrix[flavour]).sort((a, b) => Math.abs(Number(a) - Number(weight || 1)) - Math.abs(Number(b) - Number(weight || 1)));
      if (ws[0]) return { price: matrix[flavour][ws[0]], note: `at ${ws[0]} kg` };
    }
    // Any flavour at picked weight
    if (weight) {
      const matching = Object.entries(matrix).find(([, row]) => row?.[weight] != null);
      if (matching) return { price: matching[1][weight], note: `${matching[0]}` };
    }
    return null;
  }, [eggMode, flavour, weight]);

  const activePrice = computePrice(active);

  const next = () => setActiveIdx((i) => (carouselItems.length ? (i + 1) % carouselItems.length : 0));
  const prev = () => setActiveIdx((i) => (carouselItems.length ? (i - 1 + carouselItems.length) % carouselItems.length : 0));

  // ----- Touch / wheel swipe -----------------------------------------------
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 50) return;
    if (delta < 0) next(); else prev();
  };

  // ----- Cart --------------------------------------------------------------
  const addToCart = () => {
    if (!active) return;
    if (!flavour) { toast.error('Pick a flavour first'); return; }
    if (!weight)  { toast.error('Pick a weight first');  return; }
    const matrix = eggMode === 'egg' ? active.prices_egg : active.prices_eggless;
    const price = matrix?.[flavour]?.[weight];
    if (price == null) {
      toast.error(`${active.vendor.business_name} doesn't offer ${flavour} ${eggMode} at ${weight} kg`);
      return;
    }
    const itemId = `cake-${active.id}-${eggMode}-${flavour}-${weight}`;
    if (cart.find((c) => c.id === itemId)) { toast.info('Already in your cart'); return; }
    setCart([...cart, {
      id: itemId,
      name: `${active.name} — ${flavour} ${eggMode === 'egg' ? 'Egg' : 'Eggless'} ${weight}kg`,
      vendor_name: active.vendor.business_name,
      vendor_id: active.vendor.vendor_id,
      price: Number(price),
      category: 'cake',
      meta: { cake_pkg_id: active.id, flavour, weight_kg: weight, egg_mode: eggMode },
    }]);
    toast.success('Added to estimate');
  };

  const inCart = active && !!cart.find((c) => c.id?.startsWith(`cake-${active.id}-`));

  // ----- Chip props bundle (shared between inline + lightbox) -------------
  const chipProps = {
    hasEgg, hasEggless,
    eggMode, setEggMode,
    allWeights, weight, setWeight,
    allFlavours, flavour, setFlavour,
  };

  // ----- Render ------------------------------------------------------------
  return (
    <>
      <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-cake">
        <CardHeader className="cursor-pointer" onClick={onToggle}>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Cake size={20} className="text-brand-primary" weight="fill" /> Birthday Cakes
              {!loading && (
                <span className="text-xs font-normal text-text-secondary ml-1">
                  ({carouselItems.length} match{carouselItems.length === 1 ? '' : 'es'})
                </span>
              )}
            </CardTitle>
            {expanded ? <CaretUp size={18} /> : <CaretDown size={18} />}
          </div>
        </CardHeader>

        {expanded && (
          <CardContent className="space-y-4">
            {loading && <p className="text-sm text-text-secondary text-center py-4">Loading cakes…</p>}

            {!loading && allCakes.length === 0 && (
              <p className="text-sm text-text-secondary text-center py-4">
                No cake vendors verified in {eventCity || 'your city'} yet.
                We&apos;re adding bakeries — check back soon!
              </p>
            )}

            {!loading && allCakes.length > 0 && (
              <>
                {/* Single swipeable cake card */}
                <div className="relative border border-[#E8E1D5] overflow-hidden bg-stone-50"
                  data-testid="cake-carousel">
                  {/* Filter-relaxed banner */}
                  {filterMode !== 'strict' && filterMode !== 'empty' && (
                    <div className="bg-amber-50 border-b border-amber-200 px-3 py-1.5 text-[11px] text-amber-800 text-center"
                      data-testid="cake-relaxed-banner">
                      Showing closest matches — exact combo isn&apos;t available with every bakery
                    </div>
                  )}

                  <div
                    className="relative aspect-[16/10] bg-stone-200 select-none cursor-zoom-in"
                    onClick={() => active && setLightboxOpen(true)}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    data-testid="cake-active-image"
                  >
                    {active?.image_url ? (
                      <img src={resolveImg(active.image_url)} alt={active.name}
                        loading="lazy"
                        className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-stone-400">
                        <Cake size={64} />
                      </div>
                    )}

                    {/* Prev / next arrows */}
                    {carouselItems.length > 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); prev(); }}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/55 hover:bg-black/75 text-white p-2 rounded-full"
                          aria-label="Previous cake"
                          data-testid="cake-prev-btn">
                          <CaretLeft size={18} weight="bold" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); next(); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/55 hover:bg-black/75 text-white p-2 rounded-full"
                          aria-label="Next cake"
                          data-testid="cake-next-btn">
                          <CaretRight size={18} weight="bold" />
                        </button>
                      </>
                    )}

                    {/* Position pill */}
                    {carouselItems.length > 1 && (
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/55 text-white text-[11px] px-2 py-0.5 rounded-full"
                        data-testid="cake-position">
                        {activeIdx + 1} / {carouselItems.length}
                      </div>
                    )}
                  </div>

                  {/* Active cake meta strip */}
                  {active && (
                    <div className="p-3 bg-white">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-heading text-lg truncate" data-testid="cake-active-name">{active.name}</div>
                          <div className="text-[11px] text-text-secondary truncate">
                            by <strong>{active.vendor.business_name}</strong> · {active.vendor.city}
                            {(active.vendor.city || '').toLowerCase() === (eventCity || '').toLowerCase() && (
                              <span className="ml-1 text-emerald-700 font-bold">· Closest to you</span>
                            )}
                            {active.min_lead_time_hours ? <span> · {active.min_lead_time_hours}h lead</span> : null}
                            {active.delivery_included ? <span> · Free delivery</span> : null}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          {activePrice == null ? (
                            <span className="text-[11px] text-amber-700">Not offered at this combo</span>
                          ) : typeof activePrice === 'object' ? (
                            <>
                              <div className="font-bold text-brand-primary" data-testid="cake-active-price">₹{Number(activePrice.price).toLocaleString('en-IN')}</div>
                              <div className="text-[10px] text-amber-700">{activePrice.note}</div>
                            </>
                          ) : (
                            <div className="font-bold text-brand-primary text-lg" data-testid="cake-active-price">₹{Number(activePrice).toLocaleString('en-IN')}</div>
                          )}
                        </div>
                      </div>

                      <Button onClick={addToCart}
                        disabled={!active || inCart || activePrice == null || typeof activePrice === 'object'}
                        className="w-full mt-3 rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-50"
                        data-testid="cake-add-btn">
                        {inCart
                          ? <><Check size={14} className="mr-1" /> Added</>
                          : <><Plus size={14} className="mr-1" /> Add to estimate</>}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Selection chips */}
                <SelectionChips {...chipProps} />
              </>
            )}
          </CardContent>
        )}
      </Card>

      {/* Full-screen lightbox */}
      {lightboxOpen && active && (
        <div
          className="fixed inset-0 z-[80] bg-black/90 flex flex-col"
          data-testid="cake-lightbox"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          role="dialog"
          aria-modal="true"
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-3 right-3 z-10 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full"
            aria-label="Close"
            data-testid="cake-lightbox-close">
            <XIcon size={20} weight="bold" />
          </button>

          <div className="flex-1 relative flex items-center justify-center">
            {active.image_url ? (
              <img src={resolveImg(active.image_url)} alt={active.name}
                className="max-w-full max-h-full object-contain" />
            ) : (
              <Cake size={120} className="text-white/40" />
            )}
            {carouselItems.length > 1 && (
              <>
                <button onClick={prev}
                  className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white p-3 rounded-full"
                  aria-label="Previous cake"
                  data-testid="cake-lightbox-prev">
                  <CaretLeft size={24} weight="bold" />
                </button>
                <button onClick={next}
                  className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white p-3 rounded-full"
                  aria-label="Next cake"
                  data-testid="cake-lightbox-next">
                  <CaretRight size={24} weight="bold" />
                </button>
              </>
            )}
          </div>

          {/* Bottom panel: name + price + chips + add button */}
          <div className="bg-white p-4 max-h-[55vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="min-w-0">
                <div className="font-heading text-lg truncate">{active.name}</div>
                <div className="text-xs text-text-secondary truncate">
                  by <strong>{active.vendor.business_name}</strong> · {active.vendor.city}
                </div>
              </div>
              <div className="text-right shrink-0">
                {typeof activePrice === 'number' && (
                  <div className="font-bold text-brand-primary text-xl">₹{Number(activePrice).toLocaleString('en-IN')}</div>
                )}
                {typeof activePrice === 'object' && activePrice && (
                  <>
                    <div className="font-bold text-brand-primary text-xl">₹{Number(activePrice.price).toLocaleString('en-IN')}</div>
                    <div className="text-[10px] text-amber-700">{activePrice.note}</div>
                  </>
                )}
              </div>
            </div>
            <SelectionChips {...chipProps} compact />
            <Button onClick={addToCart}
              disabled={!active || inCart || activePrice == null || typeof activePrice === 'object'}
              className="w-full mt-3 rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-50"
              data-testid="cake-lightbox-add-btn">
              {inCart
                ? <><Check size={14} className="mr-1" /> Added</>
                : <><Plus size={14} className="mr-1" /> Add to estimate</>}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default CakeSegment;
